import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/use-language';
import { useEvents } from '@/hooks/use-events';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Trophy, Users, Clock, MapPin, Phone, UserX, Target, UserCheck, Edit } from 'lucide-react';
import { getGroupColorClasses, getAdminColorClasses, getLocalizedGroupName } from '@/utils/groupColors';
import { formatEventTime } from '@/utils/dateUtils';
import { EditEventDialog } from '@/components/admin/EditEventDialog';

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { events, isLoading, removeParticipant } = useEvents();
  const { user } = useAuth();
  
  const isAdmin = user?.isAdmin || false;

  const eventId = id || '0';
  const event = events.find(e => e.id === eventId);
  
  // State for tracking participant pending removal
  const [participantPendingRemoval, setParticipantPendingRemoval] = useState<string | null>(null);
  
  // State for edit dialog
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  
  // Auto-detect tournament type based on event title/description
  const getDefaultTournamentType = (): 'americano' | 'mexicano' | 'team_americano' => {
    if (!event) return 'americano';
    
    const title = event.titleKey ? t(event.titleKey) : event.title;
    const description = event.descriptionKey ? t(event.descriptionKey) : event.description;
    
    if (title.toLowerCase().includes('mexicano') || description.toLowerCase().includes('mexicano')) {
      return 'mexicano';
    } else if (title.toLowerCase().includes('team') || description.toLowerCase().includes('team')) {
      return 'team_americano';
    }
    return 'americano';
  };

  const [selectedTournamentType, setSelectedTournamentType] = useState<'americano' | 'mexicano' | 'team_americano'>(getDefaultTournamentType());

  // Determine if we can start the selected tournament type with current participants
  const canStartTournament = (() => {
    const count = event?.participants.length || 0;
    if (selectedTournamentType === 'mexicano') {
      return count >= 4; // Mexicano supports unlimited courts (4+ players)
    }
    // Americano/Team Americano: allow 4+; bench extras to nearest multiple of 4 each round
    return count >= 4;
  })();

  // Scroll to top when component mounts or when navigated to
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [eventId]);

  // Legacy numeric ID support: if id looks numeric and no event found,
  // redirect to the first available tournament event.
  useEffect(() => {
    if (!event && id && /^\d+$/.test(id)) {
      const firstTournament = events.find(e => e.type === 'tournament');
      if (firstTournament) {
        navigate(`/tournament/${firstTournament.id}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading tournament...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Tournament Not Found</h2>
          <p className="text-muted-foreground">
            The tournament you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate('/')} className="mt-4">
            {t("nav.back")} to Events
          </Button>
        </div>
      </div>
    );
  }

  const handleStartTournament = () => {
    // Navigate to the appropriate tournament page based on selection
    const participantDetails = event.participantDetails || [];
    
    const routePath = selectedTournamentType === 'mexicano' ? '/mexicano' : '/americano';
    
    navigate(routePath, { 
      state: { 
        participantDetails: participantDetails,
        fromTournamentId: event.id, // Track where we came from for back navigation
        tournamentType: selectedTournamentType
      }
    });
  };

  const handleRemoveParticipantClick = (participantId: string) => {
    if (participantPendingRemoval === participantId) {
      // Second click - actually remove the participant
      removeParticipant(event.id, participantId);
      setParticipantPendingRemoval(null);
    } else {
      // First click - set as pending removal
      setParticipantPendingRemoval(participantId);
      // Auto-cancel after 5 seconds
      setTimeout(() => {
        setParticipantPendingRemoval(null);
      }, 5000);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/')} className="shrink-0">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t("nav.back")}
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
            {event.titleKey ? t(event.titleKey) : event.title}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
            {event.descriptionKey ? t(event.descriptionKey) : event.description}
          </p>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={() => setEditEventDialogOpen(true)} className="shrink-0">
            <Edit className="h-4 w-4 mr-2" />
            Edit Event
          </Button>
        )}
      </div>

      {/* Event Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-green-600" />
            Tournament Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Date & Time:</span>
              <span>{formatDate(event.date)} at {formatEventTime(event.time)}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Location:</span>
              <span>{event.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Participants:</span>
              <Badge variant="secondary">
                {event.participants.length} / {event.maxParticipants}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Waiting List:</span>
              <Badge variant="outline">
                {event.waitingList?.length || 0} waiting
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Participants List */}
      <Card>
        <CardHeader className="flex flex-col space-y-4">
          <div>
            <CardTitle className="text-lg sm:text-xl">Registered Participants</CardTitle>
            <CardDescription className="text-sm">
              {event.participants.length} players registered for the tournament
            </CardDescription>
          </div>
          {canStartTournament && (
            <div className="flex flex-col gap-3 w-full">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Tournament Type:</label>
                <Select value={selectedTournamentType} onValueChange={(value: 'americano' | 'mexicano' | 'team_americano') => setSelectedTournamentType(value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select tournament type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="americano">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-green-500" />
                        Americano
                      </div>
                    </SelectItem>
                    <SelectItem value="mexicano">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-blue-500" />
                        Mexicano
                      </div>
                    </SelectItem>
                    <SelectItem value="team_americano">
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-purple-500" />
                        Team Americano
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleStartTournament} 
                className="bg-green-600 hover:bg-green-700 w-full text-sm px-4 py-2"
              >
                <Trophy className="h-4 w-4 mr-2" />
                Start {selectedTournamentType === 'mexicano' ? 'Mexicano' : selectedTournamentType === 'team_americano' ? 'Team Americano' : 'Americano'}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3">
            {event.participantDetails?.map((participant, index) => (
              <div key={participant.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-sm sm:text-base truncate">{participant.name}</div>
                    {participant.isAdmin && (
                      <Badge variant="admin" className="text-xs">
                        Admin
                      </Badge>
                    )}
                    {/* Color dots for group membership */}
                    {participant.groups && (
                      <div className="flex items-center gap-1">
                        {participant.groups.map((group) => {
                          const colors = getGroupColorClasses(group);
                          const localizedGroupName = getLocalizedGroupName(group, language);
                          return (
                            <div
                              key={group}
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: `hsl(var(--group-${colors.variant}))` }}
                              title={localizedGroupName}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1 truncate">
                    <Phone className="h-3 w-3 shrink-0" />
                    <span className="truncate">{participant.phone}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:shrink-0">
                  <Badge variant="secondary" className="text-xs">
                    #{index + 1}
                  </Badge>
                  {isAdmin && (
                    <Button
                      variant={participantPendingRemoval === participant.id ? "destructive" : "outline"}
                      size="sm"
                      className={
                        participantPendingRemoval === participant.id 
                          ? "bg-red-600 text-white hover:bg-red-700 text-xs px-3" 
                          : "text-destructive hover:text-destructive"
                      }
                      onClick={() => handleRemoveParticipantClick(participant.id)}
                    >
                      {participantPendingRemoval === participant.id ? (
                        <span className="flex items-center gap-1">
                          <UserX className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">Confirm Remove</span>
                          <span className="sm:hidden">Confirm</span>
                        </span>
                      ) : (
                        <UserX className="h-3 w-3 sm:h-4 sm:w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Waiting List */}
      {event.waitingListDetails && event.waitingListDetails.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Waiting List</CardTitle>
            <CardDescription>
              {event.waitingListDetails.length} players on the waiting list
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {event.waitingListDetails.map((participant, index) => (
                <div key={participant.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{participant.name}</div>
                      {participant.isAdmin && (
                        <Badge variant="admin" className="text-xs">
                          Admin
                        </Badge>
                      )}
                      {/* Color dots for group membership */}
                      {participant.groups && (
                        <div className="flex items-center gap-1">
                          {participant.groups.map((group) => {
                            const colors = getGroupColorClasses(group);
                            const localizedGroupName = getLocalizedGroupName(group, language);
                            return (
                              <div
                                key={group}
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: `hsl(var(--group-${colors.variant}))` }}
                                title={localizedGroupName}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {participant.phone}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Position #{index + 1}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Event Dialog */}
      {isAdmin && (
        <EditEventDialog
          open={editEventDialogOpen}
          onOpenChange={setEditEventDialogOpen}
          event={event}
          onEventUpdated={() => window.location.reload()}
          onEventDeleted={() => navigate('/')}
        />
      )}
    </div>
  );
}
